import { memo, useCallback, useState } from 'react';
import {
  Avatar,
  Flex,
  Box,
  Square,
  Table,
  Badge,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useToast,
} from '@chakra-ui/react';
import { useNavigate, Link } from 'react-router-dom';
import { t } from '@lingui/core/macro';
import { SquarePen } from 'lucide-react';
import { ResourceStatusBadge } from '@duke-hq/ui/components/ResourceStatusBadge/ResourceStatusBadge';
import { constructQueryParams } from '@duke-hq/lib';
import { SortableHeader } from '@duke-hq/ui/components/SortableHeader/SortableHeader';
import { djs } from '@duke-hq/djs';
import type { RouterOutputs } from '@duke-hq/svc-api/admin';
import { trpcAdmin } from '@duke-hq/app-admin-web/lib/trpcAdmin';
import { AssessmentOutcome } from '@duke-hq/svc-api-db';
import { downloadPdfAsFile } from '@duke-hq/ui/components/PdfGeneratorButton/utils';
import { buildPdfMediaResolver } from '@duke-hq/ui/components/PdfGeneratorButton/AssessmentReport/usePdfMediaResolver';
import { getTenantContext } from '@duke-hq/libctx';
import { AssignAdminDropdown, type AdminUser } from '../components/AssignAdminDropdown';
import { SortOrder } from '@duke-hq/lib/pagination';
import { ContractTypes } from '@duke-hq/shared-constants/enrolments';
import { useCheckPermission } from '@duke-hq/app-admin-web/hooks/useCheckPermission';
import { AssessmentActionsMenu } from './AssessmentActionsMenu';
import { AssessmentOutcomeSchema } from '@duke-hq/svc-api-db/schemas/enums/AssessmentOutcome.schema';

interface SortPropsInterface {
  orderBy?: string | undefined;
  sort?: SortOrder | undefined;
  page?: string | undefined;
  outcome?: string | undefined;
  courseIds?: string | undefined;
  assessorIds?: string | undefined;
  contractIds?: string | undefined;
  search?: string | undefined;
}

type AssessmentItemType = RouterOutputs['assessments']['all']['items'][number];

export function AssessmentsList({
  assessments,
  urlParams,
  onFilters,
  getProviderName,
  users,
}: {
  assessments: AssessmentItemType[];
  urlParams: SortPropsInterface;
  onFilters: (filters: SortPropsInterface, sort: boolean) => void;
  getProviderName: (providerId: string) => string | undefined;
  users: AdminUser[];
}) {
  const canDownloadAssessmentReports = useCheckPermission('canDownloadAssessmentReports');
  const navigate = useNavigate();
  const { outcome, sort, orderBy, courseIds, assessorIds, contractIds, page, search } = urlParams;

  const handleSort = useCallback(
    (name: string) => {
      const newSortDirection = sort === SortOrder.ASC ? SortOrder.DESC : SortOrder.ASC;
      const updatedFilters = {
        orderBy: name,
        sort: newSortDirection,
        outcome: outcome ?? undefined,
        courseIds: courseIds ?? undefined,
        assessorIds: assessorIds ?? undefined,
        contractIds: contractIds ?? undefined,
        search: search ?? undefined,
      } as SortPropsInterface;

      onFilters({ ...updatedFilters }, true);
    },
    [assessorIds, contractIds, courseIds, onFilters, outcome, search, sort],
  );

  const handleClick = useCallback(
    (id: number) => {
      const { page, orderBy, sort, outcome, courseIds, assessorIds, search } = urlParams;
      const baseUrl = `/assessments/${id}`;
      navigate(
        `${baseUrl}${constructQueryParams({ page, orderBy, sort, outcome, courseIds, assessorIds, search })}`,
      );
    },
    [urlParams, navigate],
  );

  const toast = useToast();
  const utils = trpcAdmin.useUtils();
  const [downloadingSubmissionId, setDownloadingSubmissionId] = useState<number | null>(null);

  const handleDownloadReport = useCallback(
    async (id: number) => {
      if (downloadingSubmissionId !== null) {
        return;
      }

      setDownloadingSubmissionId(id);
      try {
        const data = await utils.assessments.getAuditReportData.fetch({
          assessmentSubmissionId: id,
        });

        const sanitisedStudentName = data.studentName
          .replace(/[^a-zA-Z0-9-_ ]/g, '')
          .replace(/\s+/g, '-');
        const sanitisedUnitCode = data.unitCode.replace(/[^a-zA-Z0-9-_]/g, '');
        const date = djs().format('YYYY-MM-DD');
        const filename = `assessment-audit-${sanitisedStudentName}-${sanitisedUnitCode}-${date}.pdf`;

        const { fileUrls, ...report } = data;
        const reportData = { ...report, studentEmail: report.studentEmail ?? '' };
        const mediaResolver = buildPdfMediaResolver(
          (fileDrn, raw) => (typeof fileDrn === 'string' ? fileUrls[fileDrn] : undefined) ?? raw,
          getTenantContext()?.apis.FILES_API_ENDPOINT,
        );
        await downloadPdfAsFile('assessmentReport', reportData, filename, mediaResolver);
      } catch {
        toast({
          title: t`Error downloading report`,
          description: t`An error occurred while generating the assessment report. Please try again.`,
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      } finally {
        setDownloadingSubmissionId(null);
      }
    },
    [downloadingSubmissionId, toast, utils],
  );

  return (
    <Table>
      <Thead>
        <Tr>
          <Th>
            <SortableHeader
              label={t`Assessor`}
              columnName="assessed_by"
              currentOrderBy={orderBy}
              currentSortOrder={sort}
              onSortChange={handleSort}
            />
          </Th>
          <Th>
            <SortableHeader
              label={t`Participant`}
              columnName="participant"
              currentOrderBy={orderBy}
              currentSortOrder={sort}
              onSortChange={handleSort}
            />
          </Th>
          <Th>
            <SortableHeader
              label={t`Unit of competency`}
              columnName="unit"
              currentOrderBy={orderBy}
              currentSortOrder={sort}
              onSortChange={handleSort}
            />
          </Th>
          <Th>
            <SortableHeader
              label={t`Attempts`}
              columnName="attempt"
              currentOrderBy={orderBy}
              currentSortOrder={sort}
              onSortChange={handleSort}
            />
          </Th>
          <Th>
            <SortableHeader
              label={t`Status`}
              columnName="outcome"
              currentOrderBy={orderBy}
              currentSortOrder={sort}
              onSortChange={handleSort}
            />
          </Th>
          <Th>
            <SortableHeader
              label={t`Days elapsed`}
              columnName="submitted_at"
              currentOrderBy={orderBy}
              currentSortOrder={sort}
              onSortChange={handleSort}
            />
          </Th>
          <Th>
            <SortableHeader
              label={t`Contract type`}
              columnName="contract"
              currentOrderBy={orderBy}
              currentSortOrder={sort}
              onSortChange={handleSort}
            />
          </Th>
          <Th>
            <SortableHeader
              label={t`Provider`}
              columnName="provider"
              currentOrderBy={orderBy}
              currentSortOrder={sort}
              onSortChange={handleSort}
            />
          </Th>
          {canDownloadAssessmentReports === true ? (
            <Th position="sticky" right={0} zIndex={2} bg="white !important" px={8}>
              <Box
                position="absolute"
                top={0}
                left={0}
                right={0}
                bottom={0}
                boxShadow="-4px 0px 6px rgba(0, 0, 0, 0.1)"
                display="flex"
                justifyContent="center"
                alignItems="center"
              >
                <SquarePen width={20} height={20} />
              </Box>
            </Th>
          ) : null}
        </Tr>
      </Thead>
      <Tbody>
        {Array.isArray(assessments) && assessments.length !== 0
          ? assessments.map(
              (assessmentSubmission: AssessmentItemType) =>
                assessmentSubmission.latestAssessmentSubmission != null &&
                assessmentSubmission.latestAssessmentSubmission?.id != null && (
                  <AssessmentListItem
                    key={assessmentSubmission.latestAssessmentSubmission.id}
                    to={`/assessments/${assessmentSubmission.latestAssessmentSubmission.id}${constructQueryParams({ page, orderBy, sort, outcome, search })}`}
                    assessmentSubmission={assessmentSubmission}
                    onClick={handleClick}
                    handleDownloadReport={handleDownloadReport}
                    isDownloading={
                      downloadingSubmissionId === assessmentSubmission.latestAssessmentSubmission.id
                    }
                    getProviderName={getProviderName}
                    canDownloadAssessmentReports={canDownloadAssessmentReports ?? false}
                    users={users}
                  />
                ),
            )
          : null}
      </Tbody>
    </Table>
  );
}

const AssessmentListItem = memo(function AssessmentListItem({
  assessmentSubmission,
  onClick,
  handleDownloadReport,
  isDownloading,
  to,
  getProviderName,
  canDownloadAssessmentReports,
  users,
}: {
  to: string;
  assessmentSubmission: AssessmentItemType;
  onClick: (id: number) => void;
  handleDownloadReport: (id: number) => void;
  isDownloading: boolean;
  getProviderName: (id: string) => string | undefined;
  canDownloadAssessmentReports: boolean;
  users: AdminUser[];
}) {
  const utils = trpcAdmin.useUtils();
  const assignAssessment = trpcAdmin.assessments.assignAssessment.useMutation({
    onSettled: () => {
      void utils.assessments.all.invalidate(undefined, { refetchType: 'active' });
    },
  });
  const studentName = `${assessmentSubmission?.latestAssessmentSubmission.student?.first_name} ${assessmentSubmission?.latestAssessmentSubmission.student?.last_name}`;

  const handleClick = useCallback(() => {
    if (assessmentSubmission.latestAssessmentSubmission.id === undefined) {
      return;
    }
    onClick(assessmentSubmission.latestAssessmentSubmission.id);
  }, [onClick, assessmentSubmission]);

  const handleDownload = useCallback(() => {
    if (assessmentSubmission.latestAssessmentSubmission.id === undefined) {
      return;
    }

    void handleDownloadReport(assessmentSubmission.latestAssessmentSubmission.id);
  }, [handleDownloadReport, assessmentSubmission]);

  let colorScheme = 'red';
  switch (assessmentSubmission.assessmentSubmissionAttempts) {
    case 1:
      colorScheme = 'green';
      break;
    case 2:
      colorScheme = 'orange';
      break;
    default:
      colorScheme = 'red';
      break;
  }

  const canAssignAssessor =
    assessmentSubmission.latestAssessmentSubmission.outcome === AssessmentOutcome.PENDING;

  return (
    <Tr onClick={handleClick} cursor="pointer" _hover={{ bg: 'bg.subtle' }}>
      <Td>
        <AssignAdminDropdown
          currentAssignee={assessmentSubmission.latestAssessmentSubmission.assessedBy}
          onAssign={(assessorDrn) => {
            if (assessmentSubmission.latestAssessmentSubmission.id === undefined) {
              return;
            }
            assignAssessment.mutate({
              assessmentSubmissionId: assessmentSubmission.latestAssessmentSubmission.id,
              assessorDrn,
            });
          }}
          enabled={canAssignAssessor}
          users={users}
        />
      </Td>
      <Td>
        <Link to={to}>
          <Flex>
            <Flex align="center" justify="center">
              <Box>
                <Square size="32px">
                  <Avatar
                    size="sm"
                    name={studentName}
                    src={
                      assessmentSubmission?.latestAssessmentSubmission.student.picture_url ??
                      undefined
                    }
                  />
                </Square>
              </Box>
            </Flex>
            <Flex direction="column" ml={4}>
              <Box>
                <Text fontWeight="medium">{studentName}</Text>
              </Box>
              <Flex align="center" color="fg.muted" gap={1}>
                <Text>
                  {assessmentSubmission?.latestAssessmentSubmission.student.email ??
                    'No email available'}
                </Text>
              </Flex>
            </Flex>
          </Flex>
        </Link>
      </Td>
      <Td>
        <Flex direction="column">
          <Box>
            <Text>{assessmentSubmission?.unitOfCompetency.title}</Text>
          </Box>
          <Flex align="center" color="fg.muted" gap={1}>
            <Text>{assessmentSubmission?.unitOfCompetency.code ?? '-'}</Text>
          </Flex>
        </Flex>
      </Td>
      <Td>
        <Badge
          fontSize="xs"
          fontWeight="bold"
          borderRadius="full"
          colorScheme={colorScheme}
          variant="outline"
          textTransform="none"
        >
          {assessmentSubmission.assessmentSubmissionAttempts}
        </Badge>
      </Td>
      <Td>
        <ResourceStatusBadge
          status={AssessmentOutcomeSchema.parse(
            assessmentSubmission?.latestAssessmentSubmission.outcome,
          )}
        />
      </Td>
      <Td>
        <Text color="fg.muted">
          {djs().to(djs(assessmentSubmission?.latestAssessmentSubmission.submitted_at))}
        </Text>
      </Td>
      <Td>
        <Text fontWeight="fg.muted">
          {ContractTypes.find((c) => c.value === assessmentSubmission.contractType)?.label ??
            assessmentSubmission.contractType}
        </Text>
      </Td>
      <Td>
        <Text fontWeight="fg.muted">
          {getProviderName(String(assessmentSubmission.tenantId)) ?? 'N/A'}
        </Text>
      </Td>
      {canDownloadAssessmentReports === true ? (
        <Td position="sticky" right={0} zIndex={1} bg="bg.surface" px={8}>
          <Box
            position="absolute"
            top={0}
            left={0}
            right={0}
            bottom={0}
            boxShadow="-4px 0px 6px rgba(0, 0, 0, 0.1)"
            display="flex"
            justifyContent="center"
            alignItems="center"
          >
            <AssessmentActionsMenu
              variant="table"
              stopPropagation
              isDownloading={isDownloading}
              onDownloadReport={handleDownload}
            />
          </Box>
        </Td>
      ) : null}
    </Tr>
  );
});
